import { useState } from 'react';
import {
  Warning as AlertTriangle,
  Briefcase,
  Clock,
  ChatCircle as MessageSquare,
  Microphone as Mic,
  Plus,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState } from '@/components/layout/EmptyState';
import { useInterviews, type NeuraxInterview } from '@/lib/interviews/useInterviews';
import { ScheduleSessionWizard } from '@/components/sessions/ScheduleSessionWizard';
import { SessionDetailDialog } from '@/components/sessions/SessionDetailDialog';
import { cn } from '@/lib/utils/cn';

export default function InterviewsPage() {
  const { data, isLoading, isError, error } = useInterviews('interview');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [detailSession, setDetailSession] = useState<NeuraxInterview | null>(null);

  return (
    <>
      <PageHeader
        eyebrow="Sessions"
        title="Interview sessions"
        description="Schedule new interviews and review past sessions powered by NeuraX."
        actions={
          <button
            onClick={() => setScheduleOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-neurax-electric to-neurax-plasma px-4 py-2.5 text-sm font-semibold text-white shadow-glow-soft transition hover:brightness-110"
          >
            <Plus className="h-4 w-4" />
            Schedule interview
          </button>
        }
      />

      {isError && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4" />
          <span>{(error as Error)?.message || 'Failed to load interview history.'}</span>
        </div>
      )}

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              data-testid="interview-skeleton"
              className="glass-card h-24 animate-pulse"
            />
          ))}
        </div>
      )}

      {!isLoading && (data?.length ?? 0) === 0 && (
        <div data-testid="interviews-empty">
          <EmptyState
            title="No interview sessions yet"
            description="Schedule your first interview to start preparing your candidates."
            action={
              <button
                onClick={() => setScheduleOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-neurax-electric to-neurax-plasma px-4 py-2.5 text-sm font-semibold text-white shadow-glow-soft transition hover:brightness-110"
              >
                <Plus className="h-4 w-4" />
                Schedule interview
              </button>
            }
          />
        </div>
      )}

      {!isLoading && (data?.length ?? 0) > 0 && (
        <div className="space-y-3">
          {data!.map((s) => (
            <InterviewRow key={s.id} s={s} onOpen={() => setDetailSession(s)} />
          ))}
        </div>
      )}

      {scheduleOpen && (
        <ScheduleSessionWizard
          open
          onClose={() => setScheduleOpen(false)}
          sessionType="interview"
        />
      )}

      <SessionDetailDialog
        sessionId={detailSession?.id ?? null}
        session={detailSession}
        onClose={() => setDetailSession(null)}
      />
    </>
  );
}

function InterviewRow({ s, onOpen }: { s: NeuraxInterview; onOpen: () => void }) {
  const initials = (s.end_client?.name || '?')
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <button
      type="button"
      data-testid={`interview-row-${s.id}`}
      onClick={onOpen}
      className="glass-card w-full cursor-pointer p-5 text-left transition hover:border-neurax-electric/30 hover:bg-white/[0.05]"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-neurax-electric/40 to-neurax-plasma/40 text-xs font-semibold text-white">
            {initials || <Mic className="h-4 w-4" />}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">
              {s.end_client?.name || 'Unknown candidate'}
            </p>
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Briefcase className="h-3 w-3" />
                {s.position_title || 'Untitled role'}
              </span>
              <span className="text-muted-foreground/40">·</span>
              <span>{s.company_name || 'Company TBD'}</span>
            </p>
          </div>
        </div>

        <span className="flex items-center gap-2">
          {s.rescheduled_at && (
            <span
              data-testid="row-rescheduled-badge"
              title={
                s.original_interview_date
                  ? `Originally ${new Date(s.original_interview_date).toLocaleString()}`
                  : 'Rescheduled once'
              }
              className="inline-flex items-center rounded-full border border-neurax-amber/30 bg-neurax-amber/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-neurax-amber"
            >
              Rescheduled
            </span>
          )}
          <SessionStatus status={s.status} />
        </span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
        <Stat
          icon={<Clock className="h-3 w-3" />}
          label="When"
          value={s.interview_date ? fmtTime(s.interview_date) : '—'}
        />
        <Stat
          icon={<Clock className="h-3 w-3" />}
          label="Duration"
          value={fmtDuration(s.duration_seconds)}
        />
        <Stat
          icon={<MessageSquare className="h-3 w-3" />}
          label="Messages"
          value={s.message_count?.toString() ?? '—'}
        />
        <Stat label="Created" value={fmtDate(s.created_at)} />
      </div>
    </button>
  );
}

function Stat({ icon, label, value }: { icon?: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-lg border border-white/5 bg-white/[0.02] p-2.5">
      <p className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
        {icon}
        {label}
      </p>
      <p className="mt-0.5 truncate text-white">{value}</p>
    </div>
  );
}

function SessionStatus({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string; dot: string }> = {
    completed: {
      label: 'Completed',
      cls: 'border-neurax-mint/30 bg-neurax-mint/10 text-neurax-mint',
      dot: 'bg-neurax-mint shadow-[0_0_8px_rgba(52,211,153,0.8)]',
    },
    active: {
      label: 'Live',
      cls: 'border-neurax-electric/30 bg-neurax-electric/10 text-neurax-electric',
      dot: 'bg-neurax-electric shadow-[0_0_8px_rgba(91,124,255,0.8)] animate-pulse',
    },
    in_progress: {
      label: 'In progress',
      cls: 'border-neurax-electric/30 bg-neurax-electric/10 text-neurax-electric',
      dot: 'bg-neurax-electric shadow-[0_0_8px_rgba(91,124,255,0.8)] animate-pulse',
    },
    pending: {
      label: 'Scheduled',
      cls: 'border-neurax-amber/30 bg-neurax-amber/10 text-neurax-amber',
      dot: 'bg-neurax-amber',
    },
    failed: {
      label: 'Failed',
      cls: 'border-destructive/30 bg-destructive/10 text-destructive',
      dot: 'bg-destructive',
    },
  };
  const cfg = map[status] ?? {
    label: status.charAt(0).toUpperCase() + status.slice(1),
    cls: 'border-white/10 bg-white/[0.04] text-muted-foreground',
    dot: 'bg-muted-foreground',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider',
        cfg.cls
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', cfg.dot)} />
      {cfg.label}
    </span>
  );
}

function fmtDuration(s: number | null) {
  if (!s) return '—';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m === 0) return `${sec}s`;
  return sec ? `${m}m ${sec}s` : `${m}m`;
}

function fmtTime(s: string) {
  return new Date(s).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function fmtDate(s: string) {
  return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
